import type { SortKind, SortValue } from "./tableTypes";

function isEmptySortValue(value: SortValue): boolean {
  if (value == null || value === "") {
    return true;
  }
  if (typeof value === "number" && Number.isNaN(value)) {
    return true;
  }
  if (value instanceof Date && Number.isNaN(value.getTime())) {
    return true;
  }
  return false;
}

function toSortNumber(value: SortValue, kind: SortKind): number {
  if (value instanceof Date) {
    return value.getTime();
  }
  if (typeof value === "number") {
    return value;
  }
  if (kind === "date") {
    const parsed = Date.parse(String(value));
    return Number.isNaN(parsed) ? Number.NaN : parsed;
  }
  return Number(value);
}

/** Compare two sort values; empty/null/NaN values sort last in ascending order. */
export function compareSortValues(
  left: SortValue,
  right: SortValue,
  kind: SortKind = "text",
): number {
  const leftEmpty = isEmptySortValue(left);
  const rightEmpty = isEmptySortValue(right);
  if (leftEmpty && rightEmpty) {
    return 0;
  }
  if (leftEmpty) {
    return 1;
  }
  if (rightEmpty) {
    return -1;
  }

  if (kind === "text") {
    return String(left).localeCompare(String(right), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  }

  const leftNumber = toSortNumber(left, kind);
  const rightNumber = toSortNumber(right, kind);
  if (Number.isNaN(leftNumber) && Number.isNaN(rightNumber)) {
    return 0;
  }
  if (Number.isNaN(leftNumber)) {
    return 1;
  }
  if (Number.isNaN(rightNumber)) {
    return -1;
  }
  return leftNumber - rightNumber;
}

export interface SortableColumnConfig<T, SortKey extends string> {
  sortKey: SortKey;
  sortKind?: SortKind;
  getSortValue: (row: T) => SortValue;
}

export function createColumnCompare<T, SortKey extends string>(
  columns: SortableColumnConfig<T, SortKey>[],
): (a: T, b: T, column: SortKey) => number {
  const byKey = new Map<SortKey, SortableColumnConfig<T, SortKey>>();
  for (const column of columns) {
    byKey.set(column.sortKey, column);
  }

  return (a, b, column) => {
    const config = byKey.get(column);
    if (!config) {
      return 0;
    }
    return compareSortValues(
      config.getSortValue(a),
      config.getSortValue(b),
      config.sortKind ?? "text",
    );
  };
}
